import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import PageHero from "../components/PageHero";
import ProcessSection from "../components/ProcessSection";
import CreatorBooking from "../components/CreatorBooking";
import TestimonialsStrip from "../components/TestimonialsStrip";
import SEO from "../components/SEO";
import { SERVICES } from "../data/mock";

const SERVICE_CTAS = [
  "Positionierung anfragen",
  "Content-Produktion starten",
  "Betreuung anfragen",
  "Wachstum starten",
];

const LeistungenPage = () => {
  return (
    <>
      <SEO
        title="Leistungen – Content, Strategie & Performance"
        description="Positionierung, Content Creation, Social Media Betreuung und Performance Marketing. Vier Bausteine für messbare Ergebnisse auf Social Media."
        path="/leistungen"
        keywords="Social Media Leistungen, Content Creation Agentur, UGC Produktion, Video-Ads, TikTok Ads, Performance Marketing, Positionierung, Redaktionsplan"
      />
      <PageHero
        eyebrow="Leistungen"
        title="Alles, was eure Marke"
        gradient="sichtbar macht."
        subtitle="Von Positionierung bis Performance. Vier Bausteine, ein Ziel: Eine Marke, die im Feed gesehen, verstanden und gebucht wird."
      />

      {/* Big alternating service modules */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 space-y-6 md:space-y-8">
          {SERVICES.map((s, idx) => {
            const Icon = Icons[s.icon] || Icons.Sparkles;
            const dark = idx % 2 === 1;
            return (
              <article
                key={s.id}
                className={`relative grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12 lg:p-14 rounded-[32px] overflow-hidden transition-all duration-500 ${
                  dark
                    ? "bg-neutral-950 text-white"
                    : "bg-neutral-50 text-neutral-950 border border-black/5"
                }`}
              >
                {dark && (
                  <>
                    <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(236,72,153,0.22),transparent_70%)]" />
                    <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(249,115,22,0.18),transparent_70%)]" />
                  </>
                )}

                <div className="relative lg:col-span-7">
                  <div className="flex items-center gap-4">
                    <span
                      className={`font-display font-extrabold text-6xl md:text-7xl tracking-tight ${
                        dark
                          ? "text-white/15"
                          : "text-neutral-200"
                      }`}
                    >
                      0{s.id}
                    </span>
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        dark
                          ? "bg-white/10 border border-white/10"
                          : "bg-white border border-black/5"
                      }`}
                    >
                      <Icon
                        size={22}
                        className={dark ? "text-pink-300" : "text-pink-500"}
                      />
                    </div>
                  </div>

                  <h2
                    className={`mt-5 font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] ${
                      dark ? "text-white" : "text-neutral-950"
                    }`}
                  >
                    {s.title}
                  </h2>
                  <p
                    className={`mt-2 font-medium ${
                      dark
                        ? "bg-gradient-to-r from-pink-300 via-orange-300 to-purple-300 bg-clip-text text-transparent"
                        : "text-pink-500"
                    }`}
                  >
                    {s.subtitle}
                  </p>
                  <p
                    className={`mt-5 text-base md:text-lg leading-relaxed max-w-2xl ${
                      dark ? "text-neutral-300" : "text-neutral-700"
                    }`}
                  >
                    {s.description}
                  </p>
                </div>

                <div className="relative lg:col-span-5 flex flex-col gap-5">
                  <ul className="space-y-3">
                    {s.bullets.map((b, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-3 text-base ${
                          dark ? "text-neutral-200" : "text-neutral-800"
                        }`}
                      >
                        <span
                          className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${
                            dark
                              ? "bg-gradient-to-br from-pink-500 via-orange-500 to-purple-500"
                              : "bg-neutral-950"
                          }`}
                        >
                          <Check size={13} className="text-white" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/kontakt"
                    className={`mt-2 inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full text-sm font-bold tracking-[0.12em] uppercase transition-all duration-300 group ${
                      dark
                        ? "bg-white text-neutral-950 hover:bg-gradient-to-r hover:from-pink-400 hover:via-orange-400 hover:to-purple-400 hover:text-white"
                        : "bg-neutral-950 text-white hover:bg-neutral-800"
                    }`}
                  >
                    {SERVICE_CTAS[idx] || "Jetzt anfragen"}
                    <ArrowRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <ProcessSection />
      <TestimonialsStrip max={2} eyebrow="Kunden über die Zusammenarbeit" />
      <CreatorBooking />

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="p-10 md:p-16 rounded-[32px] bg-neutral-50 border border-black/5 text-center">
            <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-[-0.03em] text-neutral-950">
              Du weißt nicht, welcher Baustein zu dir passt?
            </h2>
            <p className="mt-4 text-lg text-neutral-600 max-w-2xl mx-auto">
              In einem 30-Min-Erstgespräch finden wir genau das heraus – ohne
              Verkaufsdruck.
            </p>
            <Link
              to="/kontakt"
              className="mt-8 inline-flex items-center gap-3 px-8 py-5 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold tracking-[0.12em] uppercase hover:shadow-[0_18px_42px_-10px_rgba(236,72,153,0.7)] hover:-translate-y-[2px] transition-all duration-300 group"
            >
              Kostenloses Erstgespräch
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LeistungenPage;
